<template>
  <div>
    <router-link
      :to="productLink(product)"
      data-testid="productLink"
      v-observe-visibility="{
        callback: viewHandlerTabsTile,
        once: true
      }"
      class="row mb20"
      @click.native="productClick"
    >
      <span v-if="isOnSale" class="badge sale">{{ $t('On Sale') }}</span>
      <span v-else-if="isNew" class="badge new">{{ $t('New') }}</span>
      <span v-else-if="isBestseller" class="badge bestseller">{{ $t('Bestseller') }}</span>
      <div
        class="product-image relative bg-cl-main-smoke col-xs-12 col-md-4"
        v-on="$listeners"
      >
        <img
          v-show="showPlaceholder"
          src="/assets/placeholder.svg"
          alt="placeholder"
          height="138px"
          width="120px"
          class="product-image__placeholder"
        >
        <img
          v-if="!highQualityImageError || isOnline"
          v-show="showHighQuality"
          v-lazy="thumbnailObj(product).src"
          alt="product image"
          height="138px"
          width="120px"
          @load="imageLoaded('high', true)"
          @error="imageLoaded('high', false)"
          class="product-image__thumb"
        >
      </div>
      <div
        class="product-price col-xs-12 col-md-8 h4"
      >
        <p class="cl-black h5">
          {{ product.name }}
        </p>
        <span
          class="cl-black weight-600"
          v-if="bundleMinPrice && !bundleSpecialMinPrice"
        ><span v-show="!isBundleOptionNotChangeable">od:</span> {{ bundleMinPrice | price(storeView) }}</span>
        <span
          class="price-special weight-600"
          :class="isSaleDifference && omnibusPrice ? 'cl-burnt-sienna' : 'cl-black'"
          v-if="(bundleSpecialMinPrice || product.special_price && parseFloat(product.original_price_incl_tax) > 0)"
        >{{ specialPrice | price(storeView) }}</span>
        <span
          class="price-original ml5 cl-matterhorn fs16"
          v-if="(bundleSpecialMinPrice || product.special_price && parseFloat(product.special_price) > 0) && isSaleDifference && omnibusPrice"
        >{{ omnibusPrice | price(storeView) }}</span>
        <span
          class="cl-black weight-600"
          v-if="!bundleMinPrice && !product.special_price && parseFloat(product.price_incl_tax) > 0"
        >{{ product.price_incl_tax | price(storeView) }}</span>
      </div>
    </router-link>
  </div>
</template>

<script>
import Vue from 'vue'
import { formatProductLink } from '@vue-storefront/core/modules/url/helpers'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { productThumbnailPath } from '@vue-storefront/core/helpers'
import config from 'config'
import { onlineHelper } from '@vue-storefront/core/helpers'
import { categoryResolver } from 'theme/helpers/categoryResolverFunction'

import { getBundleOptionsMinPrices } from '@vue-storefront/core/modules/catalog/helpers/bundleOptions'
import { round } from 'mathjs'
import { isSpecialPriceActive } from '@vue-storefront/core/modules/catalog/helpers/taxCalc'
import { getNextProduct } from 'src/modules/google-tag-manager/hooks/afterRegistration'

export default {
  name: 'ProductTabsTile',
  props: {
    product: {
      type: Object,
      default: null
    },
    list: {
      type: String,
      required: true,
      default: 'catalog'
    },
    omnibusPrice: {
      type: Number,
      default: 0
    },
    position: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      loaded: false,
      placeholder: '/assets/placeholder.svg',
      lowerQualityImage: false,
      lowerQualityImageError: false,
      highQualityImage: false,
      highQualityImageError: false,
      basic: true
    }
  },
  computed: {
    isSaleDifference () {
      if (!this.specialPrice || !this.omnibusPrice) return false
      const productPrice = Number(this.specialPrice.toFixed(2))
      if ((this.omnibusPrice - productPrice) <= 0) return false
      return true
    },
    isOnline () {
      return onlineHelper.isOnline
    },
    storeView () {
      return currentStoreView()
    },
    isOnSale () {
      return this.product.special_price && parseFloat(this.product.special_price) > 0 && this.isSaleDifference && this.omnibusPrice
    },
    isNew () {
      const currentDate = new Date()
      // eslint-disable-next-line no-trailing-spaces
      let expireDate = this.product.news_to_date 
        ? Date.parse(this.product.news_to_date)
        : 'none'
      return ((expireDate !== 'none') && (currentDate.getTime() < expireDate))
    },
    isBestseller () {
      return !!this.product.bestseller
    },
    showPlaceholder () {
      return !this.showLowerQuality && !this.showHighQuality
    },
    showLowerQuality () {
      return this.lowerQualityImage && !this.showHighQuality
    },
    showHighQuality () {
      return this.highQualityImage
    },
    bundleSpecialMinPrice () { // bundle special min price
      const bundleSpecialPrice = this.product.original_special_price / 100 || 0
      return bundleSpecialPrice && isSpecialPriceActive(this.product.special_from_date, this.product.special_to_date)
        ? getBundleOptionsMinPrices(this.product, this.product.price_type).map(optionPrice => round(optionPrice * bundleSpecialPrice, 2)).reduce((memo, current) => memo + current, 0)
        : 0
    },
    bundleMinPrice () { // bundle min price
      return getBundleOptionsMinPrices(this.product, this.product.price_type).reduce((memo, current) => memo + current, 0)
    },
    isBundleOptionNotChangeable () {
      if (this.product.type_id !== 'bundle') return false
      return this.product.bundle_options.every(option => option && option.product_links.length <= 1)
    },
    specialPrice () {
      return this.bundleSpecialMinPrice || this.product.price_incl_tax
    },
    originalPrice () {
      return this.bundleMinPrice || this.product.original_price_incl_tax
    }
  },
  watch: {
    lowerQualityImage (state) {
      if (state) {
        this.basic = this.$refs.lQ.naturalWidth < this.$refs.lQ.naturalHeight;
      }
    }
  },
  methods: {
    thumbnailObj (product) {
      return {
        src: this.thumbnail(product),
        loading: this.placeholder,
        error: this.placeholder
      }
    },
    getLabelFor (type) {
      if (this.listByCode && this.listByCode[type]) {
        let items = this.listByCode[type].options && this.listByCode[type].options.filter(option => option.value === String(this.prod[type]))
        return (items && items.length) && items[0].label
      }
    },
    thumbnail (product) {
      let thumbnail = productThumbnailPath(product)
      return this.getThumbnail(thumbnail, 116, 120)
    },
    productLink (product) {
      return formatProductLink(product, currentStoreView().storeCode)
    },
    imageLoaded (type, success = true) {
      this.loaded = true
      this[`${type}QualityImage`] = success
      this[`${type}QualityImageError`] = !success
    },
    viewHandlerTabsTile (isVisible, entry) {
      if (window.dataLayer) {
        if (isVisible) {
          let price = this.product.special_price_incl_tax !== 0 ? this.product.special_price_incl_tax : this.product.price_incl_tax
          price = Math.abs(parseFloat(price)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          let { deepestCategory, mainCategory, categoriesIdsFromResolver } = categoryResolver(this.product.category, this.product.main_category, this.$store)
          Vue.gtm.trackEvent({
            event: 'uaevent',
            gender: this.getLabelFor('gender') ? this.getLabelFor('gender') : '',
            eventCategory: 'Ecommerce',
            eventAction: 'Product Impressions',
            ecommerce: {
              currencyCode: 'PLN', // TODO: get currency from state
              impressions: [{
                name: this.product.name,
                id: this.product.parentSku,
                price: price,
                brand: this.getLabelFor('brand') ? this.getLabelFor('brand') : '',
                dimension19: mainCategory,
                dimension20: deepestCategory ? deepestCategory.join(',') : '',
                category: mainCategory,
                product_category_ids: categoriesIdsFromResolver,
                product_category_names: deepestCategory || [],
                list: this.list,
                position: this.position + 1
              }]
            }
          })
          Vue.prototype.$googleTagManager.impressions({
            impressions: [
              getNextProduct({
                item: this.product,
                list: this.list,
                list_id: this.product.parentSku,
                position: this.position
              })
            ]
          })
        }
      }
    },
    productClick () {
      if (window.dataLayer) {
        let price = this.product.special_price_incl_tax !== 0 ? this.product.special_price_incl_tax : this.product.price_incl_tax
        price = Math.abs(parseFloat(price)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        let { deepestCategory, mainCategory, categoriesIdsFromResolver } = categoryResolver(this.product.category, this.product.main_category, this.$store)
        const inStock = this.product.stock && typeof this.product.stock.is_in_stock === 'boolean'
          ? this.product.stock.is_in_stock
            ? 'Tak'
            : 'Nie'
          : 'Nie'
        const source = this.product.stock && this.product.stock.source_code
        window.dataLayer.push({
          event: 'uaevent',
          gender: this.getLabelFor('gender') ? this.getLabelFor('gender') : '',
          eventCategory: 'Ecommerce',
          eventAction: 'Product Click',
          ecommerce: {
            click: {
              actionField: {
                list: this.list
              },
              products: [
                {
                  name: this.product.name,
                  metric1: this.product.regular_price,
                  dimension10: source || '',
                  dimension11: this.isBestseller ? 'Tak' : 'Nie',
                  dimension12: this.isOnSale ? 'Tak' : 'Nie',
                  dimension14: this.isNew ? 'Tak' : 'Nie',
                  dimension15: this.product.type_id,
                  dimension16: inStock ? 'Tak' : 'Nie',
                  dimension17: '',
                  dimension18: this.product.author || '',
                  dimension19: mainCategory,
                  dimension20: deepestCategory ? deepestCategory.join(',') : '',
                  id: this.product.parentSku,
                  price: this.bundleMinPrice || price,
                  brand: this.getLabelFor('brand') ? this.getLabelFor('brand') : '',
                  category: mainCategory,
                  product_category_ids: categoriesIdsFromResolver,
                  product_category_names: deepestCategory || [],
                  position: this.position + 1
                }]
            }
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';

$burnt-sienna: color(burnt-sienna);
$forest-green: color(forest-green);
$main: color(main);

.price-original {
  text-decoration: line-through;
  text-decoration-color: #CFCFCF;
  -webkit-text-decoration-color: #cfcfcf;
  @media (max-width: 767px) {
    white-space: nowrap;
    text-decoration-color: #CFCFCF;
    -webkit-text-decoration-color: #cfcfcf;
  }
}

.tabs__product {
  a {
    @media (max-width: 767px) {
      margin: 0;
      padding: 0 10px;
    }
    .badge {
      &.sale {
        background: $burnt-sienna;
      }
      &.bestseller {
        background: $forest-green;
      }
      &.new {
        background: $main;
      }
      color: white;
      position: absolute;
      top: 10px;
      left: 10px;
      z-index: 1;
      line-height: 1;
      padding: 4px 6px;
      display: flex;
      -ms-flex-pack: center;
      justify-content: center;
      -ms-flex-align: center;
      align-items: center;
      border-radius: 50px;
      font-weight: 500;
      font-size: 11px;
    }
    .product-image {
      @media only screen and (min-device-width : 768px) and (max-device-width : 1024px) {
        height: auto;
        margin-top: auto;
        margin-bottom: auto;
        display: flex;
        padding: 8px;
      }
      &__thumb {
        @media only screen and (min-device-width : 768px) and (max-device-width : 1024px) {
          height: auto;
          width: 100%;
        }
        object-fit: contain;
        height: 120px;
        width: 100%;
      }
      padding-top: 20px;
      padding-bottom: 20px;
      text-align: center;
      border-radius: 10px;
      @media (max-width: 767px) {
        background-color: white;
        padding-left: 0;
        padding-right: 0;
        padding-bottom: 10px;
        padding-top: 10px;
      }
    }
    .product-price {
      @media only screen and (min-device-width : 768px) and (max-device-width : 1024px) {
        padding: 10px 15px 0 15px;
        p {
          max-height: 65px;
          overflow: hidden;
          margin: 0;
        }
      }
      padding-top: 20px;
      padding-left: 30px;
      padding-right: 30px;
      @media (max-width: 767px) {
        border-top: 1px solid #F2F3F4;
        text-align: left;
        padding: 10px;
        font-size: 14px;
      }
      p {
        @media (max-width: 767px) {
          font-size: 12px;
          margin-top: 0;
          overflow: hidden;
          max-height: 28px;
        }
      }
    }
  }
  @media (max-width: 767px) {
    -ms-flex: 1 1;
    flex: 1 1;
    text-align: center;
    background-color: white;
    min-width: 150px;
    width: 150px;
    margin-right: 5px;
    margin-left: 5px;
    border-radius: 20px;
  }
}
</style>
